import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import type { AccessibleOU } from '@/models/FuelUserAccess';
import { LocalStorageService } from '@/services/local-storage';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import { treeViewUtils } from '@/utils';
import { cacheUtils } from '@/utils/cacheUtils';

import FuelTreeSelectDropDown from '../fuel-tree-select-dropdown/FuelTreeSelectDropDown';

const OuSwitcher = () => {
  const { accessibleOUs } = useSelector(fuelUserAccessSliceSelector);
  const { closeWebSocket } = useWebSocketContext();
  const { fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const { defaultLoginOu: defaultUserOu } = fuelUserAccessData || {};

  const navigate = useNavigate();

  const accessibleOUList = useMemo(() => {
    const finalData = treeViewUtils.createDataTree(accessibleOUs);
    treeViewUtils.assignCount(finalData);
    return finalData;
  }, [accessibleOUs]);

  const selectedOu = useMemo(() => {
    if (!accessibleOUs) return null;

    let defaultOU: AccessibleOU | undefined;

    // First go to the Local Storage
    const defaultLoginOu = LocalStorageService.getItem('defaultLoginOu');
    if (defaultLoginOu) {
      defaultOU = accessibleOUs.find((o) => o.ouId === defaultLoginOu);
    }

    // Next try the fuelUserAccessData.defaultLoginOu
    if (!defaultOU && defaultUserOu) {
      defaultOU = accessibleOUs.find((o) => o.ouId === defaultLoginOu);
      if (defaultOU && defaultOU.ouId) {
        // console.log('setting defaultLoginOu to', defaultOU.ouId);
        LocalStorageService.setItem('defaultLoginOu', defaultOU.ouId);
      }
    }

    // Next just get the first ou if nothing else
    if (!defaultOU) {
      const finalData = treeViewUtils.createDataTree(accessibleOUs);
      [defaultOU] = finalData;
      if (defaultOU && defaultOU.ouId) {
        // console.log('setting defaultLoginOu to', defaultOU.ouId);
        LocalStorageService.setItem('defaultLoginOu', defaultOU.ouId);
      }
    }

    // console.log('getSelectedOu found', defaultOU.ouId);
    return defaultOU;
  }, [accessibleOUs, defaultUserOu]);

  const handleSwitchOu = async (ouId: string) => {
    // console.log('handleSwitchOu', { value });
    if (ouId) {
      const location: string = window.location.pathname;
      // console.log('handleSwitchOu', { location });
      LocalStorageService.setItem('defaultLoginOu', ouId);

      // close websocket connection
      closeWebSocket();

      // Special conditions for navigating away from the editor screens before refreshing
      if (location) {
        if (location.startsWith('/dashboards/usage')) {
          // /channelmanager/editor/83ace869-43b6-4044-87e3-17308855cd28
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/dashboards/usage');
          return;
        }
        if (location.startsWith('/channelmanager/editor')) {
          // /channelmanager/editor/83ace869-43b6-4044-87e3-17308855cd28
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/channelmanager/list');
          return;
        }
        if (location.startsWith('/live/programs/editor')) {
          // /channelmanager/live/programs/editor/eaadda7c-c352-4fe7-927c-cd4bc53abcbc
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/live/programs/list');
          return;
        }
        if (location.startsWith('/transitiontemplates/editor')) {
          // eg. /channelmanager/transitiontemplates/editor/e7df3d3d-746c-48f0-86d0-fa509fcd8acc
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/transitiontemplates/list');
          return;
        }
        if (location.startsWith('/series/editor')) {
          // eg. /channelmanager/transitiontemplates/editor/e7df3d3d-746c-48f0-86d0-fa509fcd8acc
          LocalStorageService.setItem('RELOAD_ON_NAVIGATION', true);
          navigate('/series/list');
          return;
        }
      }

      // For all other pages we can simply refresh the page
      LocalStorageService.removeItem('RELOAD_ON_NAVIGATION');
      cacheUtils.doHardRefresh();
    }
  };

  if (!accessibleOUList) return null;

  return (
    <FuelTreeSelectDropDown
      edit={false}
      newData={accessibleOUList}
      label="Switch Ou"
      selectedValue={selectedOu}
      onChange={handleSwitchOu}
    />
  );
};

export default OuSwitcher;
